// @flow
import * as React from 'react';
import Modal from 'react-modal';

import { sftpCredentialsMock } from 'admin/components/AdminAutoMessageSftpCredentialsModal';
import AdminNewAutoMessageFormSidebar from './AdminNewAutoMessageFormSidebar.jsx';
import Button from 'components/Button.jsx';
import ConfirmDialog from 'components/ConfirmDialog.jsx';
import WizardLayout from 'components/WizardLayout';

import type { WizardStepNode } from 'components/WizardSteps';
import type { AutoMessage, AutoMessageOrg, AutoMessageTab, AutoMessageTabKey } from './';

type Props = {
  onBack: () => void,
  onFieldChange: () => void,
  onResetFieldChange: () => void,
  onSave: (fields: *, onSuccess: Function) => void,
  orgs: AutoMessageOrg[],
  selectedOrg: ?AutoMessageOrg,
  autoMessageFields: AutoMessage,
  tabs: AutoMessageTab[],
  startingTab: ?*,
};

type State = {
  currentTab: AutoMessageTab,
  confirmationDialog: ?{
    futureTabKey: AutoMessageTabKey,
    show: boolean,
  },
  fieldsHaveBeenChanged: boolean,
};

export default class AdminNewAutoMessageForms extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const startingTab =
      props.startingTab && props.tabs.find(tab => tab.tabKey === props.startingTab);

    this.state = {
      currentTab: startingTab || props.tabs[0],
      confirmationDialog: undefined,
      fieldsHaveBeenChanged: false,
    };
  }

  advanceTab = () => {
    const { nextStep } = this.state.currentTab;
    const newTab = this.props.tabs.find(tab => tab.tabKey === nextStep);
    this.resetFieldChangeState();
    this.setState({ currentTab: newTab });
  };

  backtrackTab = () => {
    const { prevStep } = this.state.currentTab;
    const newTab = this.props.tabs.find(tab => tab.tabKey === prevStep);
    this.resetFieldChangeState();
    this.setState({ currentTab: newTab });
  };

  jumpToTab = (tabKey: AutoMessageTabKey) => {
    const currentTab = this.props.tabs.find(tab => tab.tabKey === tabKey);
    this.resetFieldChangeState();
    this.setState({ currentTab });
  };

  hideConfirmationDialog = () => {
    this.setState({
      confirmationDialog: undefined,
    });
  };

  showConfirmationDialog = (futureTabKey: AutoMessageTabKey) => {
    this.setState({
      confirmationDialog: {
        show: true,
        futureTabKey,
      },
    });
  };

  handleFieldChange = () => {
    this.setState({ fieldsHaveBeenChanged: true });
    this.props.onFieldChange();
  };

  resetFieldChangeState = () => {
    this.setState({ fieldsHaveBeenChanged: false });
    this.props.onResetFieldChange();
  };

  renderSidebar = (selectedOrg: *) => {
    const { autoMessageFields } = this.props;
    const { currentTab, fieldsHaveBeenChanged } = this.state;

    const nodes: WizardStepNode[] = this.props.tabs.map(tab => ({
      name: tab.name,
      complete: tab.isComplete,
      active: currentTab.tabKey === tab.tabKey,
      nodeKey: tab.tabKey,
    }));

    if (!selectedOrg) {
      console.error('AdminAutoMessageForms - tried to render without selecting an org');
      return null;
    }

    return (
      <AdminNewAutoMessageFormSidebar
        messageName={autoMessageFields.messageName}
        status={autoMessageFields.status}
        selectedOrgName={selectedOrg.name}
        nodes={nodes}
        onClickNode={tabKey => {
          if (fieldsHaveBeenChanged) {
            this.showConfirmationDialog(tabKey);
            return;
          }
          this.jumpToTab(tabKey);
        }}
      />
    );
  };

  render() {
    const { orgs, autoMessageFields, selectedOrg, tabs } = this.props;
    const {
      uuid,
      messageName,
      orgUuid,
      csvName,
      sftpCredentials,
      messageContent,
      sender,
      includeParents,
      includeStudents,
      status,
      isActivatable,
    } = autoMessageFields;
    const { confirmationDialog, currentTab } = this.state;
    const Component = currentTab.component;
    const formatCSVTab = tabs.find(tab => tab.tabKey === 'format-csv');
    const syncDataTab = tabs.find(tab => tab.tabKey === 'sync-data');
    const composeTab = tabs.find(tab => tab.tabKey === 'compose');

    return (
      <React.Fragment>
        {confirmationDialog && (
          <Modal isOpen={confirmationDialog.show}>
            <ConfirmDialog
              actionButtonText="Discard"
              cancelButtonText="Cancel"
              onCancel={this.hideConfirmationDialog}
              onConfirm={() => this.jumpToTab(confirmationDialog.futureTabKey)}
              onRequestHide={this.hideConfirmationDialog}
              title="Your changes will be discarded. Are you sure you want to continue?"
            />
          </Modal>
        )}
        <WizardLayout
          wrapInView={false}
          headerIcon="lightning-bolt"
          headerIconColor="shamrock"
          title="New auto message"
          onBack={this.props.onBack}
          nav={
            <Button href="#" color="polar-bear" level="secondary" size="xsmall">
              Help
            </Button>
          }
          sidebar={this.renderSidebar(selectedOrg)}>
          <Component
            isActivatable={isActivatable}
            messageUuid={uuid}
            messageName={messageName}
            orgUuid={orgUuid}
            csvName={csvName}
            sftpCredentials={sftpCredentials}
            messageContent={messageContent}
            sender={sender}
            includeParents={includeParents}
            includeStudents={includeStudents}
            status={status}
            selectedOrg={selectedOrg}
            // TODO: replace with actual sftp creds
            possibleSftpCredentials={[sftpCredentialsMock]}
            potentialOrgs={orgs}
            onSave={this.props.onSave}
            onAdvance={this.advanceTab}
            onBack={this.backtrackTab}
            onJump={this.jumpToTab}
            onClose={this.props.onBack}
            onChangeField={this.handleFieldChange}
            onResetFieldChange={this.resetFieldChangeState}
            formatCSVComplete={formatCSVTab && formatCSVTab.isComplete}
            syncDataComplete={syncDataTab && syncDataTab.isComplete}
            composeComplete={composeTab && composeTab.isComplete}
          />
        </WizardLayout>
      </React.Fragment>
    );
  }
}
